/**
 * Analytics Event Catalog
 * Centralized event names for type-safe tracking
 */

export const AnalyticsEvents = {
  // Page Views
  PAGE_VIEW: 'page_view',

  // Authentication
  LOGIN_SUCCESS: 'login_success',
  LOGIN_FAILED: 'login_failed',
  LOGOUT: 'logout',
  SIGNUP_COMPLETED: 'signup_completed',
  SIGNUP_FAILED: 'signup_failed',

  // Backend System Events (Elasticsearch)
  USER_AUTO_LOGOUT: 'user_auto_logout',
  COMMUNITIES_USER_AUTO_LOGOUT: 'communities_user_auto_logout',
  GENERATE_TOKEN_FAILED: 'generate_token_failed',
  GENERATE_TOKEN_SUCCESS: 'generate_token_success',
  COMMUNITIES_GROUP_ACCESS: 'communities_group_access',
  NO_COMMUNITIES_FOUND: 'no_communities_found',
  COURSE_ACCESS_REFRESH: 'course_access_refresh',
  PUSH_NOTIFICATION_INITIALIZED: 'push_notification_initialized',
  FORCE_UPDATE_EVENT: 'force_update_event',

  // Posts
  POST_CREATED: 'post_created',
  POST_EDITED: 'post_edited',
  POST_DELETED: 'post_deleted',
  POST_VIEWED: 'post_viewed',
  POST_SHARED: 'post_shared',

  // Comments
  COMMENT_CREATED: 'comment_created',
  COMMENT_EDITED: 'comment_edited',
  COMMENT_DELETED: 'comment_deleted',

  // Reactions
  REACTION_ADDED: 'reaction_added',
  REACTION_REMOVED: 'reaction_removed',

  // Groups
  GROUP_CREATED: 'group_created',
  GROUP_JOINED: 'group_joined',
  GROUP_LEFT: 'group_left',
  GROUP_VIEWED: 'group_viewed',

  // Channels
  CHANNEL_CREATED: 'channel_created',
  CHANNEL_VIEWED: 'channel_viewed',
  CHANNEL_JOINED: 'channel_joined',

  // Courses
  COURSE_VIEWED: 'course_viewed',
  COURSE_ENROLLED: 'course_enrolled',
  LESSON_STARTED: 'lesson_started',
  LESSON_COMPLETED: 'lesson_completed',

  // Events
  EVENT_CREATED: 'event_created',
  EVENT_VIEWED: 'event_viewed',
  EVENT_RSVP_YES: 'event_rsvp_yes',
  EVENT_RSVP_NO: 'event_rsvp_no',

  // Search
  SEARCH_PERFORMED: 'search_performed',
  SEARCH_RESULT_CLICKED: 'search_result_clicked',

  // Chat (legacy GA parity)
  CHAT_MESSAGE_SENT: 'chat_message_sent',
  CHAT_MESSAGE_FAILED: 'chat_message_failed',
  CHAT_BLOCKED: 'chat_blocked',
  CHAT_CREATED: 'chat_created',

  // GoKollab discovery (legacy GA parity)
  GOKOLLAB_HOMEPAGE_LAND: 'gokollab_homepage_land',
  GOKOLLAB_LISTING_VIEW: 'gokollab_listing_view',
  GOKOLLAB_LISTING_JOINED: 'gokollab_listing_joined',
  GOKOLLAB_COMMUNITIES_VIEW: 'gokollab_communities_view',
  GOKOLLAB_COURSES_VIEW: 'gokollab_courses_view',
  GOKOLLAB_CREATORS_VIEW: 'gokollab_creators_view',
  GOKOLLAB_CATEGORY_SWITCH: 'gokollab_category_switch',
  GOKOLLAB_COURSE_CREATE: 'gokollab_course_create',
  GOKOLLAB_COMMUNITY_CREATE: 'gokollab_community_create',

  // Courses navigation (legacy GA parity)
  LESSON_OPENED: 'lesson_opened',
  NEXT_LESSON: 'next_lesson',
  COMMUNITY_COURSE_OPENED: 'community_course_opened',

  // Social / mentions (legacy GA parity)
  FOLLOW: 'follow',
  UNFOLLOW: 'unfollow',
  COURSE_MENTIONED: 'course_mentioned',
  EVENT_MENTIONED: 'event_mentioned',
  CHANNEL_MENTIONED: 'channel_mentioned',
  GO_LIVE_SESSION_JOINED: 'go_live_session_joined',

  // Moderation / onboarding (legacy GA parity)
  USER_BANNED: 'user_banned',
  MEDIA_REMOVED: 'media_removed',
  EVENT_ATTENDEES_VIEWED: 'event_attendees_viewed',
  EVENT_ATTENDEES_LIST_EXPORTED: 'event_attendees_list_exported',
  MEMBER_VIEWED_OWN_ANSWERS: 'member_viewed_own_answers',
  ADMIN_VIEWED_MEMBER_ANSWERS: 'admin_viewed_member_answers',

  // Certificates (legacy GA parity)
  CERTIFICATE_DOWNLOAD: 'certificate_download',

  // Legacy RSVP aggregate (maps to event-rsvp-event in GA provider)
  EVENT_RSVP: 'event_rsvp',

  // Profile
  PROFILE_VIEWED: 'profile_viewed',
  PROFILE_EDITED: 'profile_edited',

  // Media
  MEDIA_UPLOADED: 'media_uploaded',
  VIDEO_PLAYED: 'video_played',
  VIDEO_COMPLETED: 'video_completed',

  // Errors
  ERROR_OCCURRED: 'error_occurred',
  API_ERROR: 'api_error',

  // UI Interactions
  ELEMENT_CLICKED: 'element_clicked',
  NAVIGATION_CLICKED: 'navigation_clicked',

  // Forms
  FORM_STARTED: 'form_started',
  FORM_COMPLETED: 'form_completed',
  FORM_ABANDONED: 'form_abandoned',
} as const

export type AnalyticsEventName = (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents]
